import React from 'react';
import { 
    Box, 
    Typography, 
    IconButton,
    Button,
    Tooltip
} from '@mui/material';
import {
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Today as TodayIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';

const WeekNavigation = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    gap: theme.spacing(2),
}));

const NavigationControls = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const TodayButton = styled(Button)(({ theme }) => ({
    minWidth: 'auto',
    padding: theme.spacing(0.5, 1),
    fontSize: '0.875rem',
}));

/**
 * WeekHeader Component
 * 
 * Displays week navigation controls and week range information.
 * Can be configured for different use cases (navigation vs display-only).
 * 
 * @param {Object} props
 * @param {Object} props.currentWeek - dayjs object representing current week
 * @param {Function} props.onPreviousWeek - Callback for previous week navigation
 * @param {Function} props.onNextWeek - Callback for next week navigation
 * @param {Function} props.onGoToToday - Callback for today button
 * @param {boolean} props.showNavigation - Whether to show navigation controls
 * @param {boolean} props.showTodayButton - Whether to show today button
 * @param {boolean} props.loading - Whether navigation should be disabled
 * @param {React.ReactNode} props.extraActions - Additional action buttons
 */
const WeekHeader = ({ 
    currentWeek,
    onPreviousWeek,
    onNextWeek,
    onGoToToday,
    showNavigation = true,
    showTodayButton = true,
    loading = false,
    extraActions = null
}) => {
    // Calculate week boundaries
    const weekStart = currentWeek.startOf('isoWeek');
    const weekEnd = currentWeek.endOf('isoWeek');
    const weekDisplayText = `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D, YYYY')}`;
    
    // Check if current week contains today
    const isCurrentWeek = dayjs().isSame(currentWeek, 'isoWeek');

    return (
        <WeekNavigation>
            {showNavigation ? (
                <NavigationControls>
                    <IconButton 
                        onClick={onPreviousWeek}
                        aria-label="Previous week"
                        size="small"
                        disabled={loading}
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                    
                    <Typography variant="h6" fontWeight="medium">
                        {weekDisplayText}
                    </Typography>
                    
                    <IconButton 
                        onClick={onNextWeek}
                        aria-label="Next week"
                        size="small"
                        disabled={loading}
                    >
                        <ChevronRightIcon />
                    </IconButton>
                </NavigationControls>
            ) : (
                <Typography variant="h6" fontWeight="medium">
                    {weekDisplayText}
                </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {showTodayButton && !isCurrentWeek && (
                    <Tooltip title="Go to current week">
                        <TodayButton
                            variant="outlined"
                            size="small"
                            startIcon={<TodayIcon />}
                            onClick={onGoToToday}
                            disabled={loading}
                        >
                            Today
                        </TodayButton>
                    </Tooltip>
                )}
                {extraActions}
            </Box>
        </WeekNavigation>
    );
};

export default WeekHeader; 